import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import ClickableDiv from 'dpl/common/components/ClickableDiv';
import SmartImage from 'dpl/components/SmartImage';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { capitalize } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import { BADGE_LIGHT_COLORS } from 'dpl/common/design_system/utils/constants';
import Icon from 'dpl/common/components/Icon';

import useBreederProfileTabs from '../../hooks/useBreederProfileTabs';
import CardBadge from '../../mobile/components/CardBadge';

const IS_LT_MD = isLessThanBreakpoint('md');
const IMAGE_SIZE = isLessThanBreakpoint('lg') ? '311px' : '276px';
const ICON_SIZE = IS_LT_MD ? '24px' : '32px';

export default function BreederProfileLitterPuppyCard({
  className,
  puppy,
  breederProfileId,
  imageHeight,
  analytics
}) {
  const { viewFeatureLocation } = useBreederProfileTabs();
  const { isLoggedIn, isBuyer } = useCurrentUser();
  const { breederDisplayName, breederFirstName, breederPhotoUrl } = useSelector(
    ({ server }) => server
  );

  const { badgeColor, badgeDescription, puppyDescription } = useMemo(() => {
    if (puppy.reserved || puppy.snoozed) {
      return {
        badgeColor: BADGE_LIGHT_COLORS.rubber,
        badgeDescription: puppy.reserved_status_for_puppy_card || 'Reserved',
        puppyDescription: capitalize(puppy.gender || '')
      };
    }

    return {
      badgeColor: BADGE_LIGHT_COLORS.teal,
      badgeDescription: puppy.ready_for_adoption_date_v2,
      puppyDescription: [puppy.age_description, capitalize(puppy.gender || '')]
        .filter(Boolean)
        .join(' · ')
    };
  }, [puppy]);

  const sharedAnalytics = {
    ...analytics,
    view: analytics.view || ANALYTICS_VIEWS.BREEDER_PROFILE,
    view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
    view_feature_location:
      analytics.view_feature_location || viewFeatureLocation,
    dog_id: puppy.id,
    breeder_profile_id: breederProfileId,
    breed_id: puppy.breed_id
  };

  const handleCardClick = useCallback(
    ({ currentTarget, target }) => {
      if (currentTarget !== target && !currentTarget.contains(target)) {
        return;
      }

      sendRudderstackEvent(
        ANALYTICS_EVENTS.CARD_CLICKED_BY_USER,
        sharedAnalytics
      );

      openPuppyPage({ puppyId: puppy.id });
    },
    [puppy, sharedAnalytics]
  );

  return (
    <ClickableDiv
      className={className}
      data-test-id={`puppy-${puppy.id}`}
      onClick={handleCardClick}
    >
      <div className="BreederProfileLitterPuppyCard h-100 flex flex-column br3 ba b--default overflow-hidden relative">
        {puppy.profile_photo_url ? (
          <SmartImage
            src={puppy.profile_photo_url}
            Tag="div"
            className="w-100 bg-center cover br3 br--top"
            loadingClass="bg-light-gray"
            height={imageHeight}
            allowContextMenu
            crop
            lazy
          />
        ) : (
          <div
            className="bg-white flex items-center justify-center bb b--default"
            style={{ height: imageHeight }}
          >
            <div className="br-100 bg-sand pv3 ph3 ph4-md pv4-md">
              <Icon
                name="fetch-dog"
                width={ICON_SIZE}
                height={ICON_SIZE}
                className="secondary"
              />
            </div>
          </div>
        )}
        <div className="white absolute w-100 top-0 right-0">
          <div className="ph4 pv4 absolute right-0">
            {(!isLoggedIn || isBuyer) && (
              <FavoriteButton
                analytics={{
                  ...sharedAnalytics,
                  breeder_name: breederDisplayName,
                  breeder_profile_id: Number(breederProfileId)
                }}
                followableTypeId={puppy.id}
                followableType={FOLLOWABLE_TYPES.DOG}
                checkedIconProps={{ name: 'fetch-heart-full-puppy' }}
                uncheckedIconProps={{
                  name: 'fetch-heart-dark',
                  className: 'default-color o-100'
                }}
                puppyName={puppy.name}
                puppyPhotoUrl={puppy.profile_photo_url}
                breederFirstName={breederFirstName}
                breederProfilePhotoUrl={breederPhotoUrl}
              />
            )}
          </div>
        </div>
        <div className="pv4 ph4 bg-white">
          <div className="flex items-center justify-between">
            <p className="font-serif font-24 truncate">{puppy.name}</p>
            {badgeDescription && (
              <CardBadge className={classnames('ml1 nowrap', badgeColor)}>
                {badgeDescription}
              </CardBadge>
            )}
          </div>

          {puppyDescription && (
            <p className="font-14 secondary">{puppyDescription}</p>
          )}
        </div>
      </div>
    </ClickableDiv>
  );
}

export const PuppyPropTypes = PropTypes.shape({
  reserved: PropTypes.bool.isRequired,
  breed_id: PropTypes.number.isRequired,
  gender: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  profile_photo_url: PropTypes.string,
  snoozed: PropTypes.bool.isRequired,
  age_description: PropTypes.string,
  ready_for_adoption_date_v2: PropTypes.string,
  reserved_status_for_puppy_card: PropTypes.string
});

BreederProfileLitterPuppyCard.propTypes = {
  className: PropTypes.string,
  breederProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  puppy: PuppyPropTypes.isRequired,
  imageHeight: PropTypes.string,
  analytics: PropTypes.shape({
    view: PropTypes.string,
    view_feature_location: PropTypes.string,
    position: PropTypes.number
  })
};

BreederProfileLitterPuppyCard.defaultProps = {
  className: null,
  imageHeight: IMAGE_SIZE,
  analytics: {}
};
